import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {choose} from 'lit/directives/choose.js';
import {router, navigator, outlet} from 'lit-element-router';

import {configureLocalization} from '@lit/localize';

import storeEngine from 'store/src/store-engine';
import localStorage from 'store/storages/localStorage';
import cookieStorage from 'store/storages/cookieStorage';
import defaultsPlugin from 'store/plugins/defaults';

import {sourceLocale, targetLocales} from '../../../generated/locales.js'; // Generated via output.localeCodesModule
import getRoutes from '../routes';
import store from '../store';

import {
	setDebug,
	setFamily,
	setLang,
	setProjectKey,
	setStatus,
	setMode,
	setMedia,
	setModeSwitchActive, setKey, setRegion, setRootKey, setIframe
} from '../slices/statusSlice';
import {fetchConfiguration} from '../slices/configurationSlice';
import config from '../config';
import {trim} from '../../../common/utils/string';
import {routeUrl} from '../util/routeUrl';
import {generateKey} from '../util/generateKey';

import * as templatesDe from '../../../generated/locales/de.js';
import * as templatesFr from '../../../generated/locales/fr.js';

import {selectSectionConfig, selectCurrentSection} from '../selectors';
import {silentLogin} from '../slices/userSlice';
import {fetchSpecsheet} from '../slices/specsheetSlice';

const localizedTemplates = new Map([
	['de', templatesDe],
	['fr', templatesFr],
]);

export const {getLocale, setLocale} = configureLocalization({
	sourceLocale: sourceLocale,
	targetLocales: targetLocales,
	// use static locales, because of small size
	loadLocale: async (locale) => localizedTemplates.get(locale)
	// loadLocale: (locale) => import('/locales/' + locale +'.js'),
});

@customElement('configurator-app')
export class ConfiguratorApp extends connect(store)(router(navigator(outlet(LitElement)))) {

	static styles = [
		css`
			:host {
				height: 100%;
				display: flex;
				flex-direction: column;
			}

			.pages {
				overflow-y: scroll;
			}

			.head {
				flex-grow: 0;
				z-index: 10;
			}

			.content {
				flex-grow: 1;
				align-self: stretch;
				position: relative;
				height: 100%;
			}
		`
	];

	// observed attribute name is "myvalue"
	static properties = {
		api: {type: String},
	};

	@property({type: String})
	host = '';

	@property({type: String})
	base = '';

	@property({type: String})
	express = '/express';

	@property({type: String})
	auth = '/users/userManagement.php';

	@property({type: String})
	engine = '';

	@property({type: String})
	pim = '';

	@property({type: String})
	pvgis = '';

	@property({type: String})
	specsheet = '';

	@property({type: String})
	shortnumber = '';

	@property({type: String})
	assets = '/ePIM';

	@property({type: String})
	channel = 'alpha';

	@property({type: String})
	features = '';

	@property({type: String})
	route = '';

	@property({type: Object})
	params = {};

	@property({type: Object})
	query = {};

	configurationAvailable = false;

	static get routes() {
		return getRoutes();
	}

	constructor() {
		super();

		setLocale('de');

		this.localStorage = storeEngine.createStore(
			[localStorage, cookieStorage],
			[defaultsPlugin]
		);
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.status = state.status;
		this.specsheetActive = state.status.specsheetActive;
		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);

	}

	router(route, params, query, data) {
		this.route = route;
		this.params = params;
		this.query = query;
		this.setStatusFromRoute();
	}
	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		} else {
			return false;
		}
	}
	setStatusFromRoute() {
		const status = store.getState().status;
		if (status) {

			if (this.api && this.api !== status.apiUrl) {
				store.dispatch(setStatus(
					{
						host: this.host,
						baseUrl: this.base,
						apiUrl: this.api,
						expressUrl: this.express,
						authUrl: this.auth,
						engineUrl: this.engine + '/engine/v1',
						pimUrl: this.pim + '/product-data-provider/v1',
						priceUrl: this.pim + '/price/v1',
						shortNumberUrl: this.shortnumber + '/short-number/v1',
						shortArticleNumberUrl: this.pim + '/short-article-number/v1',
						epimAssetsUrl: this.assets,
						pvgisUrl: this.pvgis,
						specsheetUrl: this.specsheet,
						channel: this.channel,
						features: this.features.split(',').reduce((features, cur) => {
							features[cur] = true;
							return features;
						}, {})
					}
				));
				if (this.getCookie('slxcfgsession')) {
					store.dispatch(silentLogin({}));
				}
			}
			if (this.query.iframe && this.query.iframe !== status.iframe) {
				console.log('iframe', this.query.iframe);
				store.dispatch(setIframe({iframe: this.query.iframe === 'true'}));
			}
			if (this.params.family !== status.family) {
				console.log('family', this.params.family);
				if (this.params.family === 'LightProfile') {
					store.dispatch(setFamily({family: 'LightProfile'}));
				} else {
					store.dispatch(setFamily({family: this.params.family ?? 'Start'}));
				}
			}

			if (this.params.region !== status.region) {
				console.log('region', this.params.region);
				store.dispatch(setRegion({region: this.params.region ?? 'int'}));
			}
			if (this.params.language !== status.lang) {
				console.log('lang', this.params.language);
				store.dispatch(setLang({lang: this.params.language ?? 'de'}));
			}
			if (this.params.media !== status.media) {
				console.log('media', this.params.media);
				if (this.params.media && this.params.media !== 'undefined') {
					store.dispatch(setMedia({media: this.params.media}));
				} else {
					// Add any additional actions or logic for the default media value
					store.dispatch(setMedia({media: 'sections'}));
				}
			}
			if (this.configurationAvailable && this.params.mode !== status.mode) {
				store.dispatch(setMode({mode: this.params.mode ?? 'external'}));
			}
			if (this.params.uid !== status.dataKey) {
				//await this.loadConfiguration();
				//this.setModeFromRoute();
				//await this.navigate(routeUrl());
			}
		}
	}

	setModeFromRoute() {
		const status = store.getState().status;
		if (this.params.mode !== status.mode) {
			store.dispatch(setMode({mode: this.params.mode ?? 'external'}));
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
		addEventListener('resize', this.appHeight);
		await this.loadConfiguration();
		this.setModeFromRoute();
		this.navigate(routeUrl());

	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeEventListener('keydown', this.handleKeydown);
		removeEventListener('resize', this.appHeight);
	}

	appHeight() {
		document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 68: // d
				if (e.ctrlKey) {
					this.toggleDebug();
				}
				break;
			case 73: // i
				if (e.ctrlKey) {
					this.toggleModeSwitchActive();
				}
				break;
		}
	};

	toggleDebug(e) {
		store.dispatch(setDebug(
			{
				debug: !this.status.debug
			}
		));
	}

	toggleModeSwitchActive(e) {
		store.dispatch(setModeSwitchActive(
			{
				modeSwitchActive: !this.status.modeSwitchActive
			}
		));
	}

	async loadConfiguration() {

		// get the configuration UID from route
		const keyFromRoute = this.params.uid;
		const lastUsedKey = this.getLastUsedKey();
		const lastUsedFamily = this.getLastUsedFamily();
		if (keyFromRoute === undefined) {
			// console.log('family match: ', lastUsedFamily === store.getState().status.family);
			/*
			if (lastUsedKey && lastUsedKey !== 'undefined' && lastUsedFamily === store.getState().status.family) {
				// load with local saved key
				await store.dispatch(fetchConfiguration(lastUsedKey));
				await store.dispatch(setKey({key: lastUsedKey}));
				console.log('no key, using local saved key: ', store.getState().status.dataKey);
			}*/
			// no key, new configuration from default
			const newKey = generateKey();
			await store.dispatch(setRootKey({rootKey: newKey}));
			await store.dispatch(setKey({key: newKey}));
			console.log('no key, create new key: ', store.getState().status.dataKey);
		} else {
			if (keyFromRoute === 'new') {
				const newKey = generateKey();
				await store.dispatch(setRootKey({rootKey: newKey}));
				await store.dispatch(setKey({key: newKey}));
				console.log('key = new, force new key: ', store.getState().status.dataKey);
			} else {
				// fetch the configuration
				console.log('loading existing config:', keyFromRoute);
				await store.dispatch(fetchConfiguration(keyFromRoute));
				// generate a new key and save it
				if (keyFromRoute !== lastUsedKey) {
					await store.dispatch(setRootKey({rootKey: keyFromRoute}));
					await store.dispatch(setKey({key: generateKey()}));
					console.log('branching to new key:', store.getState().status.dataKey);
				} else {
					await store.dispatch(setKey({key: keyFromRoute}));
					console.log('using key from route: ', store.getState().status.dataKey);
				}
			}

		}
		this.setLastUsedKey(store.getState().status.family, store.getState().status.dataKey);
		this.configurationAvailable = true;
	}

	getKeyFromUrl(url, baseUrl) {
		url = url.split('#')[0].split('?')[0].substr(baseUrl.length + 1);
		const trimedUrl = trim(url, '/ ').split('/');
		return trimedUrl[trimedUrl.length - 1];
	}

	updateUrl(baseUrl, media, key, replace = false) {
		const url = baseUrl + '/' + (media !== '' ? (media + '/') : '') + key;
		if (replace) {
			window.history.replaceState(null, '', url);
		} else {
			window.history.pushState(null, '', url);
		}
	}

	getLastUsedKey() {
		const savedKey = String(this.localStorage.get(config.lastUsedKeyName));
		if (savedKey.includes(':')) {
			return savedKey.split(':')[1];
		} else {
			return savedKey;
		}
	}

	getLastUsedFamily() {
		const savedKey = String(this.localStorage.get(config.lastUsedKeyName));
		if (savedKey.includes(':')) {
			return savedKey.split(':')[0];
		} else {
			return null;
		}
	}

	setLastUsedKey(modelid, key) {
		if (key && key !== 'undefined') {
			this.localStorage.set(config.lastUsedKeyName, modelid + ':' + key);
		}
	}

	async setCurrentProjectKey(key) {
		store.dispatch(setProjectKey({currentProjectKey: key}));
	}


	render() {
		const status = store.getState().status;
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);

		return html`
			<main-configurator active-route=${this.route}>
				${when(status.media === 'print',
					() => choose(this.status.family, [
							[
								'LightProfile',
								() => html`
									<div class="pages">
										<light-new-specsheet></light-new-specsheet>
									</div>
								`
							],
							[
								'TheLine',
								() => html`
									<div class="pages">
										<line-new-specsheet></line-new-specsheet>
									</div>
								`
							],
							[
								'Aeto',
								() => html`
									<div class="pages">
										<solar-new-specsheet></solar-new-specsheet>
									</div>
									`
							]
						],
						() => html`
							<div class="pages avanza">
								<avanza-new-specsheet></avanza-new-specsheet>
							</div>
						`
					),
					() => html`
						<div class="head">
							<sections-head></sections-head>
						</div>
						<div route="configuration" class="content">
							${choose(this.params.media, [
									['specsheet',
										() => html`
											<specsheet-panel></specsheet-panel>
										`],
									['summary',
										() => html`
											<summary-panel></summary-panel>
										`],
								],
								() => html`
									<configuration-sections .dashboardActive=${this.params.media === 'overview'}></configuration-sections>
								`
							)}
						</div>
						<modal-view></modal-view>
						<explain-view></explain-view>
					`)}
			</main-configurator>
		`;
	}
}
